import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import KpiCard from "@/components/KpiCard";
import { Bar, BarChart, ResponsiveContainer, Tooltip, XAxis, YAxis, CartesianGrid } from "recharts";
import { useAuth } from "@/context/AuthContext";
import { usePermissions } from "@/context/PermissionsContext";
import { Sparkles } from "lucide-react";

const INR = (n) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(n || 0);

const STAGE_LABEL = {
  new: "New", contacted: "Contacted", qualified: "Qualified",
  proposal: "Proposal", won: "Won", lost: "Lost",
};

export default function Dashboard() {
  const { user } = useAuth();
  const { has } = usePermissions();
  const canRevenue = has("dashboard_revenue", "view");
  const canLeads = has("leads", "view");
  const canQuotations = has("quotations", "view");
  const canProjects = has("projects", "view");
  const canClients = has("clients", "view");
  const canEmployees = has("employees", "view");
  const { data: kpis } = useQuery({ queryKey: ["kpis"], queryFn: async () => (await api.get("/dashboard/kpis")).data });
  const { data: pipeline } = useQuery({
    queryKey: ["pipeline"],
    queryFn: async () => (await api.get("/dashboard/pipeline")).data,
    enabled: canLeads,
  });
  const { data: trend } = useQuery({ queryKey: ["hours-trend"], queryFn: async () => (await api.get("/dashboard/hours-trend")).data });
  // M2-A12 · Personal "My Work" view for callers without company-wide visibility
  const showMyWork = !canClients;   // Designer/QA/Trainee/Sr Designer/Team Lead do NOT have clients.view
  const { data: myWork } = useQuery({
    queryKey: ["my-work"],
    queryFn: async () => (await api.get("/dashboard/my-work")).data,
    enabled: showMyWork,
  });

  const trendData = (trend?.series || []).map((d) => ({ date: d.date.slice(5), hours: d.hours }));
  const pipelineData = (pipeline?.stages || []).map((s) => ({
    stage: STAGE_LABEL[s] || s,
    count: pipeline?.counts?.[s] || 0,
    value: pipeline?.values?.[s] || 0,
  }));

  return (
    <div data-testid="dashboard-page">
      <PageHeader
        eyebrow={`Welcome back, ${user?.name?.split(" ")[0] || "there"}`}
        title="Executive Dashboard"
        description="Live view of your leads pipeline, active production, and monthly revenue signals."
      />
      <div className="max-w-[1400px] mx-auto px-8 py-8 space-y-8">
        {/* M2-A12 · Personal My Work section — SELF-tier callers */}
        {showMyWork && myWork && (
          <section className="border border-zinc-200 rounded-lg bg-white p-6 space-y-5" data-testid="my-work-card">
            <div className="flex items-baseline justify-between">
              <div>
                <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-zinc-500">My Work</div>
                <div className="font-heading text-lg font-bold mt-1">Your personal snapshot</div>
              </div>
              <div className="text-xs text-zinc-500 font-mono">week of {myWork.week_start}</div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <div><div className="text-[10px] uppercase tracking-widest text-zinc-500">Hours today</div><div className="text-2xl font-bold mt-1">{myWork.hours_today}</div></div>
              <div><div className="text-[10px] uppercase tracking-widest text-zinc-500">Hours this week</div><div className="text-2xl font-bold mt-1">{myWork.hours_week}</div></div>
              <div><div className="text-[10px] uppercase tracking-widest text-zinc-500">Approved hours</div><div className="text-2xl font-bold mt-1">{myWork.approved_hours_week}</div></div>
              <div><div className="text-[10px] uppercase tracking-widest text-zinc-500">Open personal tasks</div><div className="text-2xl font-bold mt-1">{myWork.open_tasks_count}</div></div>
              <div><div className="text-[10px] uppercase tracking-widest text-zinc-500">Pending revisions</div><div className="text-2xl font-bold mt-1">{myWork.pending_revisions_count}</div></div>
            </div>
            <div className="grid md:grid-cols-2 gap-6 pt-2 border-t border-zinc-100">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">My assigned projects</div>
                <ul className="space-y-1.5">
                  {(myWork.projects || []).map((p) => (
                    <li key={p.id} className="text-sm flex items-center justify-between">
                      <span className="font-medium text-zinc-800">{p.display_name}</span>
                      <span className="text-[10px] uppercase tracking-widest text-zinc-500">{p.status?.replace(/_/g, " ")}</span>
                    </li>
                  ))}
                  {!(myWork.projects || []).length && <li className="text-xs text-zinc-400 italic">No projects assigned yet.</li>}
                </ul>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Upcoming personal tasks</div>
                <ul className="space-y-1.5">
                  {(myWork.upcoming_tasks || []).map((t) => (
                    <li key={t.id} className="text-sm flex items-center justify-between">
                      <span className="text-zinc-800">{t.title}</span>
                      <span className="text-[11px] text-zinc-500 font-mono">{t.due_date}{t.due_time ? ` · ${t.due_time}` : ""}</span>
                    </li>
                  ))}
                  {!(myWork.upcoming_tasks || []).length && <li className="text-xs text-zinc-400 italic">No upcoming tasks.</li>}
                </ul>
              </div>
            </div>
          </section>
        )}

        {/* KPI Grid */}
        <section className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="kpi-grid">
          {canLeads && <KpiCard label="Active Leads" value={kpis?.active_leads ?? "—"} sublabel="in pipeline" testId="kpi-active-leads" />}
          {canQuotations && <KpiCard label="Open Quotations" value={kpis?.open_quotations ?? "—"} sublabel="draft · sent · nego" testId="kpi-open-quotations" />}
          {canProjects && <KpiCard label="Projects In-Production" value={kpis?.in_production_projects ?? "—"} sublabel="planning · production · QA" testId="kpi-projects" />}
          {canRevenue && <KpiCard label="Approved Quotation Value" value={INR(kpis?.approved_quotation_value)} sublabel="lifetime · per-project" testId="kpi-revenue" />}
          {canClients && <KpiCard label="Total Clients" value={kpis?.total_clients ?? "—"} testId="kpi-clients" />}
          {canEmployees && <KpiCard label="Total Employees" value={kpis?.total_employees ?? "—"} testId="kpi-employees" />}
          {canProjects && <KpiCard label="Production Hours (Month)" value={kpis?.month_production_hours ?? "—"} sublabel="this calendar month" testId="kpi-hours-month" />}
          {canProjects && <KpiCard label="Approved Hours (Month)" value={kpis?.approved_production_hours ?? "—"} sublabel="ready to invoice" testId="kpi-hours-approved" />}
        </section>

        {/* Charts */}
        <section className={`grid gap-4 ${canLeads ? "lg:grid-cols-5" : "lg:grid-cols-3"}`}>
          <div className={`${canLeads ? "lg:col-span-3" : "lg:col-span-3"} border border-zinc-200 rounded-lg bg-white p-6`} data-testid="hours-trend-card">
            <div className="flex items-baseline justify-between">
              <div>
                <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-zinc-500">Production Hours · Last 14 days</div>
                <div className="font-heading text-lg font-bold mt-1">Daily production output</div>
              </div>
              <div className="text-xs text-zinc-500 font-mono">org-wide</div>
            </div>
            <div className="h-64 mt-6 min-w-0">
              <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={220}>
                <BarChart data={trendData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" vertical={false} />
                  <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#71717a" }} tickLine={false} axisLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: "#71717a" }} tickLine={false} axisLine={false} />
                  <Tooltip cursor={{ fill: "#f4f4f5" }} contentStyle={{ borderRadius: 6, border: "1px solid #e4e4e7", fontSize: 12 }} />
                  <Bar dataKey="hours" fill="#2563eb" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
          {canLeads && (
          <div className="lg:col-span-2 border border-zinc-200 rounded-lg bg-white p-6" data-testid="pipeline-card">
            <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-zinc-500">Sales Pipeline</div>
            <div className="font-heading text-lg font-bold mt-1">Lead stages</div>
            <ul className="mt-5 space-y-3">
              {pipelineData.map((s) => (
                <li key={s.stage} className="text-sm" data-testid={`pipeline-stage-${s.stage.toLowerCase()}`}>
                  <div className="flex items-center justify-between">
                    <span className="text-zinc-700">{s.stage}</span>
                    <span className="text-zinc-500 font-mono text-xs">{s.count} · {INR(s.value)}</span>
                  </div>
                  <div className="mt-1 h-1.5 bg-zinc-100 rounded overflow-hidden">
                    <div className="h-full bg-zinc-900 rounded" style={{ width: `${Math.min(100, s.count * 18)}%` }} />
                  </div>
                </li>
              ))}
            </ul>
          </div>
          )}
        </section>

        {/* AI teaser */}
        <section className="border border-zinc-200 rounded-lg bg-zinc-950 text-white p-8 flex flex-col md:flex-row items-start md:items-center gap-6" data-testid="ai-teaser">
          <div className="w-10 h-10 rounded-md bg-white/10 grid place-items-center shrink-0">
            <Sparkles className="w-5 h-5" />
          </div>
          <div className="flex-1">
            <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-zinc-400">AI Studio · Sprint 6</div>
            <div className="font-heading text-xl font-bold mt-1">Reserved for Quotation, Email & Forecast assistants</div>
            <p className="text-sm text-zinc-300 mt-2 max-w-2xl leading-relaxed">
              Routes and schema are already provisioned. When enabled, Dream Home ERP will draft quotations from a lead brief,
              summarize weekly production, and forecast revenue — powered by Claude Sonnet, GPT, and Gemini via the Emergent LLM key.
            </p>
          </div>
        </section>
      </div>
    </div>
  );
}
